import React, {createContext, useCallback, useContext, useMemo, useState} from 'react';

interface DialogStackContextValue {
  pushDialog: (id: string) => void;
  popDialog: (id?: string) => void;
  topDialog: string | null;
}

const DialogStackContext = createContext<DialogStackContextValue>({
  pushDialog: () => {},
  popDialog: () => {},
  topDialog: null,
});

export const DialogStackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stack, setStack] = useState<string[]>([]);

  const pushDialog = useCallback((id: string) => {
    setStack(prev => [...prev.filter(item => item !== id), id]);
  }, []);

  const popDialog = useCallback((id?: string) => {
    setStack(prev => {
      if (!prev.length) {
        return prev;
      }

      if (!id) {
        return prev.slice(0, -1);
      }

      return prev.filter(item => item !== id);
    });
  }, []);

  const topDialog = stack.length > 0 ? stack[stack.length - 1] : null;
  const contextValue = useMemo(
    () => ({pushDialog, popDialog, topDialog}),
    [popDialog, pushDialog, topDialog],
  );

  return (
    <DialogStackContext.Provider value={contextValue}>
      {children}
    </DialogStackContext.Provider>
  );
};

export const useDialogStack = () => useContext(DialogStackContext);
